'use server'

import { apiRequest, ApiResponse } from '../request'

export interface HrEmployee {
    id: number
    /** Organização dona do cadastro — o RH é org-global. */
    organizationId?: number | null
    /** Filial empregadora: o CNPJ que assina a carteira e paga a folha. */
    companyId: number
    company?: { id: number; name: string } | null
    userId: number | null
    name: string
    cpf: string | null
    rg: string | null
    birthDate: string | null
    gender: string | null
    maritalStatus: string | null
    nationality: string | null
    race: string | null
    pis: string | null
    email: string | null
    phone: string | null
    whatsAppNumber: string | null
    street: string | null
    city: string | null
    state: string | null
    zipCode: string | null
    bankName: string | null
    bankAgency: string | null
    bankAccount: string | null
    /** Dígito verificador da conta — exigido pelo TED. */
    bankAccountDigit?: string | null
    photoUrl: string | null
    admissionDate: string | null
    terminationDate: string | null
    contractType: string
    workRegime: string
    weeklyHours: number | null
    jobTitle: string | null
    cboCode: string | null
    department: string | null
    costCenterId: number | null
    salary: number | null
    salaryType: string
    status: string
    terminationType: string | null
    /** Usuário do sistema vinculado (aba "Acesso ao sistema"). */
    user?: { id: number; name: string; email: string | null } | null
    /** Conta PF do funcionário na plataforma, usada para pagar o salário. */
    platformOrganizationId?: number | null
    platformOrganization?: {
        id: number
        name: string
        cpf: string | null
        paymentWalletId: string | null
        paymentAccountStatus: string | null
    } | null
    dependents?: any[]
    history?: any[]
    createdAt: string
}

const BASE = 'hr/employees'

/**
 * Quadro da ORGANIZAÇÃO — inclui quem trabalha em outras filiais.
 * `companyId` filtra por filial empregadora.
 */
export async function getHrEmployees(params?: {
    status?: string; department?: string; search?: string
    companyId?: number; page?: number; limit?: number
}): Promise<ApiResponse<HrEmployee[]>> {
    return apiRequest<HrEmployee[]>(BASE, { params: params || {} })
}

export async function getHrEmployee(id: number): Promise<ApiResponse<HrEmployee>> {
    return apiRequest<HrEmployee>(`${BASE}/${id}`, {})
}

export async function getHrEmployeeHistory(id: number): Promise<ApiResponse<any[]>> {
    return apiRequest<any[]>(`${BASE}/${id}/history`, {})
}

export async function createHrEmployee(data: any): Promise<ApiResponse<HrEmployee>> {
    return apiRequest<HrEmployee>(BASE, { data }, 'post')
}

export async function updateHrEmployee(id: number, data: any): Promise<ApiResponse<HrEmployee>> {
    return apiRequest<HrEmployee>(`${BASE}/${id}`, { data }, 'patch')
}

export async function terminateHrEmployee(id: number, data: {
    terminationType: string; terminationReason?: string; terminationDate: string
}): Promise<ApiResponse<HrEmployee>> {
    return apiRequest<HrEmployee>(`${BASE}/${id}/terminate`, { data }, 'post')
}

/** Vincula um usuário já existente ao funcionário (acesso ao sistema). */
export async function linkHrEmployeeUser(id: number, userId: number): Promise<ApiResponse<HrEmployee>> {
    return apiRequest<HrEmployee>(`${BASE}/${id}/user`, { data: { userId } }, 'post')
}

/** Remove o vínculo. O usuário continua existindo, só deixa de ser o funcionário. */
export async function unlinkHrEmployeeUser(id: number): Promise<ApiResponse<HrEmployee>> {
    return apiRequest<HrEmployee>(`${BASE}/${id}/user`, {}, 'delete')
}

/**
 * Vincula a conta PF do funcionário na plataforma, localizada pelo CPF.
 * Sem `cpf`, usa o CPF do cadastro. Com ela, o salário sai por transferência
 * entre contas do provedor — sem tarifa e imediata.
 */
export async function linkHrEmployeePlatformAccount(
    id: number,
    cpf?: string,
): Promise<ApiResponse<HrEmployee>> {
    return apiRequest<HrEmployee>(`${BASE}/${id}/platform-account`, { data: { cpf } }, 'post')
}

/** Desfaz o vínculo. O salário volta a sair pelo PIX/TED do cadastro. */
export async function unlinkHrEmployeePlatformAccount(id: number): Promise<ApiResponse<HrEmployee>> {
    return apiRequest<HrEmployee>(`${BASE}/${id}/platform-account`, {}, 'delete')
}

export async function deleteHrEmployee(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`${BASE}/${id}`, {}, 'delete')
}
